import { useCallback } from 'react'
import { gql } from '@apollo/client'
import { useTranslations } from '@npco/common-utils-translations'
import { currencyFormatter } from '@npco/component-mp-common'
import { Box, Flex, InfoBox, INFOBOX_VARIANT } from '@npco/zeller-design-system'
import { useAvailableFundsCorporateCard } from 'features/Cards/CardSingle/hooks/useAvailableFundsCorporateCard/useAvailableFundsCorporateCard'
import { useGoToEditSpendControls } from 'features/EditSpendControls/edit-spend-controls-routing'

import { useLoggedInUser } from 'hooks/useLoggedInUser'
import { CustomerRole } from 'types/gql-global-types'
import { ExpenseCardBudgetExceedErrorMessageDebitCardV2Fragment } from 'types/gql-types/ExpenseCardBudgetExceedErrorMessageDebitCardV2Fragment'
import { getIsCardRestricted } from 'components/CardSummary/components/CardDisplay/CardDisplay.utils'

import { expenseCardBudgetExceedTranslations } from './ExpenseCardBudgetExceedErrorMessage.i18n'

interface ExpenseCardBudgetExceedErrorMessageProps {
  card: ExpenseCardBudgetExceedErrorMessageDebitCardV2Fragment
  timezone?: string
}

export const ExpenseCardBudgetExceedErrorMessage = ({
  card,
  timezone,
}: ExpenseCardBudgetExceedErrorMessageProps) => {
  const t = useTranslations(expenseCardBudgetExceedTranslations)
  const { userData } = useLoggedInUser()
  const availableFunds = useAvailableFundsCorporateCard({ card, timezone })
  const { goToEditSpendControls } = useGoToEditSpendControls()
  const isAdmin = userData?.role === CustomerRole.ADMIN

  const handleOpenEditSpendControlModal = useCallback(() => {
    goToEditSpendControls({
      stage: 'edit',
      cardUuid: card.id,
      accountUuid: card.debitCardAccount.id,
      amountLimit: card.velocityControl?.amountLimit.value,
      maxTransactionValue: card.velocityControl?.maxTransactionValue.value,
      velocityWindow: card.velocityControl?.velocityWindow,
      modifiedBy: card.velocityControl?.modifiedBy ?? undefined,
    })
  }, [card, goToEditSpendControls])

  if (getIsCardRestricted(card.status)) {
    return null
  }

  if (availableFunds >= 0) {
    return null
  }

  return (
    <Box mt={{ _: '24px', XS: '40px' }}>
      <InfoBox variant={INFOBOX_VARIANT.DANGER}>
        {isAdmin ? (
          <Flex
            flexDirection={{ _: 'column', MD: 'row' }}
            gap="16px"
            justifyContent="space-between"
            width="100%"
          >
            <InfoBox.Message>
              {t('messageAdmin', {
                exceededAmount: currencyFormatter(Math.abs(availableFunds)),
              })}
            </InfoBox.Message>
            <InfoBox.ButtonGroup>
              <InfoBox.CallToAction onClick={handleOpenEditSpendControlModal}>
                {t('editButton')}
              </InfoBox.CallToAction>
            </InfoBox.ButtonGroup>
          </Flex>
        ) : (
          <InfoBox.Message>{t('messageManager')}</InfoBox.Message>
        )}
      </InfoBox>
    </Box>
  )
}

ExpenseCardBudgetExceedErrorMessage.fragments = {
  DebitCardV2: gql`
    fragment ExpenseCardBudgetExceedErrorMessageDebitCardV2Fragment on DebitCardV2 {
      id
      status
      debitCardAccount {
        id
      }
      velocityControl {
        amountLimit {
          value
        }
        maxTransactionValue {
          value
        }
        velocityWindow
        modifiedBy {
          actingCustomerUuid
          updatedAt
        }
      }
      ...UseAvailableFundsCorporateCardDebitCardV2Fragment
    }

    ${useAvailableFundsCorporateCard.fragments.DebitCardV2}
  `,
}
